import { maintenanceHttpClient } from "@/lib/maintenance-http-client";
import { ConfirmActionHost } from "@/components/ConfirmActionDialog";
import { PromotionLayout } from "@/components/marketing/PromotionModal";
import { Toaster } from "@/components/ui/sonner";
import { clearClientCache } from "@/lib/client-cache";
import { activationChecklistQueries } from "@/features/activation-checklist/activationChecklist.queries";
import { createQueryClient, invalidateQueryKeys } from "@/lib/query-client";
import { pendingRequestQueries } from "@/queries/pendingRequests.queries";
import {
  enableGoogleAnalytics,
  trackGoogleAnalyticsEventOnce,
  trackGoogleAnalyticsPageView,
} from "@/lib/google-analytics";
import { redactAnalyticsPageUrl } from "@/lib/analytics-privacy";
import { createFlashMessenger } from "@/lib/flash-messages";
import { rememberSignIn } from "@/features/sign-in/remembered-sign-in";
import { reportGatewayFailure } from "@/lib/handled-failures";
import { webSentryDataCollection, webSentryDsn } from "@/lib/sentry-dsn";
import { sentryNoiseFilters } from "@/lib/sentry-noise";
import { loadChunkOrReload, reloadOnStaleChunks } from "@/lib/stale-assets";
import { registerStoredImagesCache } from "@/lib/stored-images-cache";
import { capturePageView, loadWebAnalytics, resetWebAnalytics, stopSessionRecording } from "@/lib/posthog-analytics";
import { initializeMetaPixel, trackMetaEventOnce, trackMetaPage } from "@/lib/meta-analytics";
import { initializeSnapPixel, trackSnapEventOnce, trackSnapPage } from "@/lib/snap-analytics";
import { initializeTikTokPixel, trackTikTokEventOnce, trackTikTokPage } from "@/lib/tiktok-analytics";
import { initializeXPixel, trackXEventOnce } from "@/lib/x-analytics";
import type { SharedPageProps } from "@/types/models";
import type { Page } from "@inertiajs/core";
import { createInertiaApp, router } from "@inertiajs/react";
import type { ResolvedComponent } from "@inertiajs/react";
import { DirectionProvider } from "@radix-ui/react-direction";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { resolvePageComponent } from "laravel-vite-plugin/inertia-helpers";
import { NuqsAdapter } from "nuqs/adapters/react";
import { LazyMotion } from "framer-motion";
import { createRoot } from "react-dom/client";
import { toast } from "sonner";
import "../css/app.css";

const appName = import.meta.env.VITE_APP_DISPLAY_NAME || "أوراق";
const inertiaPageScript = document.querySelector<HTMLScriptElement>('script[data-page="app"]');
const initialPage = inertiaPageScript ? (JSON.parse(inertiaPageScript.textContent || "{}") as Page) : undefined;

const pageMeta = (name: string) => document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)?.content;

reloadOnStaleChunks();

// The page carries the backend's environment, so local production builds stay out of production's issues.
const sentryEnvironment = pageMeta("sentry-environment");

if (import.meta.env.PROD && sentryEnvironment === "production") {
  Sentry.init({
    dsn: webSentryDsn,
    environment: sentryEnvironment,
    dataCollection: webSentryDataCollection,
    integrations: [Sentry.browserTracingIntegration(), Sentry.captureConsoleIntegration({ levels: ["error"] })],
    tracesSampleRate: 0.05,
    ...sentryNoiseFilters,
  });
}

if (import.meta.env.PROD) {
  registerStoredImagesCache();
}

// Local, test and preview builds are production builds too, so only the server knows a visit is real.
const analyticsEnabled = (initialPage?.props as SharedPageProps | undefined)?.analytics?.enabled === true;

if (import.meta.env.PROD && analyticsEnabled) {
  // Analytics loads beside the first render instead of inside the entry; calls made meanwhile are queued. A chunk that
  // fails to load goes through the reload recovery above, and analytics must never interrupt the app otherwise.
  void loadWebAnalytics().catch(() => undefined);
}

// The animation features load after the first render; `m` components animate once they arrive.
const loadMotionFeatures = () =>
  loadChunkOrReload(() => import("@/lib/motion-features")).then((module) => module.default);

const queryClient = createQueryClient();

const endsSession = (url: URL) => ["/auth/logout", "/auth/demo/exit"].includes(url.pathname);

router.on("before", (event) => {
  if (endsSession(event.detail.visit.url)) {
    resetWebAnalytics();
  }
});

// Clearing the cache makes mounted queries refetch. Before the server ends the session, a refetch would carry the old
// session cookie, and if it answered after the logout, its cookie would sign the browser back in.
router.on("finish", (event) => {
  const { visit } = event.detail;

  if (endsSession(visit.url)) {
    clearClientCache(queryClient);
    return;
  }

  // Member requests and checklist steps change only through writes, so a page visit keeps their cached counts.
  if (visit.completed && visit.method !== "get") {
    void invalidateQueryKeys(queryClient, [pendingRequestQueries.all(), activationChecklistQueries.all()]);
  }
});

// The backend reports its own 5xx; a visit that died at the gateway (502/503/504) never reaches it. Returning nothing
// keeps Inertia's default handling, and a network error stays a breadcrumb because the browser was offline.
router.on("httpException", (event) => {
  reportGatewayFailure("inertia.visit", event.detail.response);
});

router.on("networkError", () => {
  Sentry.addBreadcrumb({
    category: "handled_failure",
    level: "warning",
    message: "inertia.visit",
    data: { reason: "offline" },
  });

  // Cancel Inertia's rejected network event after recording it, so offline visits do not become unhandled rejections.
  return false;
});

if (analyticsEnabled) {
  enableGoogleAnalytics();
  const analytics = (initialPage?.props as SharedPageProps | undefined)?.analytics;
  initializeTikTokPixel(analytics?.tiktokPixelId);
  initializeMetaPixel(analytics?.metaPixelId);
  initializeSnapPixel(analytics?.snapPixelId);
  initializeXPixel(analytics?.xSignupEventCode);
}

// A shallow copy keeps every other prop's identity, so the page's effects and queries do not rerun.
const showFlash = createFlashMessenger(toast, () =>
  router.replace({
    props: (props) => ({ ...props, success: null, error: null }),
    preserveScroll: true,
    preserveState: true,
  }),
);

// A redirect from outside the app, such as a payment page, lands as the initial visit; an action that redirects back to
// the same page emits only success.
router.on("success", (event) => {
  showFlash(event.detail.page);
});

// Inertia emits navigate for the initial visit, client visits, and browser history navigation.
router.on("navigate", (event) => {
  const page = event.detail.page;
  const analyticsPage = redactAnalyticsPageUrl(page.url);

  if (analyticsPage.redacted) {
    stopSessionRecording();
  }

  // Meta's and X's explicit payloads must not depend on other analytics providers or their browser storage access.
  trackSnapPage(page.url);
  trackSnapEventOnce((page.props as SharedPageProps).analyticsEvent);
  trackMetaPage(page.url);
  trackMetaEventOnce((page.props as SharedPageProps).analyticsEvent);
  trackXEventOnce((page.props as SharedPageProps).analyticsEvent);

  trackGoogleAnalyticsPageView(analyticsPage.url);
  trackGoogleAnalyticsEventOnce((page.props as SharedPageProps).analyticsEvent);

  if (!analyticsPage.redacted) {
    trackTikTokPage(page.url);
  }

  trackTikTokEventOnce((page.props as SharedPageProps).analyticsEvent);

  const firstError = Object.values(page.props.errors ?? {})[0];

  // The sign-in page explains a missing account in place, with what to do next.
  if (firstError && !page.props.accountNotFound) {
    toast.error(firstError);
  }

  showFlash(page);
  rememberSignIn((page.props as SharedPageProps).signedInWith, (page.props as SharedPageProps).user);
  capturePageView(page.component);
});

createInertiaApp({
  http: maintenanceHttpClient,
  page: initialPage,
  title: () => appName,
  resolve: (name) =>
    loadChunkOrReload(() =>
      resolvePageComponent<ResolvedComponent>(
        `./Pages/${name}.tsx`,
        // Partials and tests are not pages; globbing them would emit each as its own page chunk.
        import.meta.glob<ResolvedComponent>(["./Pages/**/*.tsx", "!./Pages/**/Partials/**", "!./Pages/**/*.test.tsx"]),
      ),
    ),
  setup({ el, App, props }) {
    createRoot(el).render(
      <QueryClientProvider client={queryClient}>
        <NuqsAdapter>
          <DirectionProvider dir="rtl">
            <LazyMotion features={loadMotionFeatures}>
              <App {...props} defaultLayout={() => [PromotionLayout]} />
              <Toaster position="top-right" />
              <ConfirmActionHost />
            </LazyMotion>
          </DirectionProvider>
        </NuqsAdapter>
      </QueryClientProvider>,
    );
  },
  progress: {
    delay: 0,
    color: "#34d399",
    showSpinner: false,
  },
});
